/**
 * Settings page "Allgemein" (`/settings/general`, feature plans 0068, 0069): settings a tenant admin saves
 * for the whole tenant — the start page the bare address `/` always opens, and under Linkview or Wissen
 * optionally one concrete Linkboard or knowledge object.
 *
 * The saved values come from `window.__TENANT_SETTINGS__` (HomeAction puts them into the page). A save
 * dispatches `save.tenant-settings`; on success the global is brought up to date, so `/` in this tab
 * opens the new start page right away.
 *
 * The options of the first list are `START_PAGE_GROUPS` (start-page.helper.ts), grouped per mode with the
 * mode switch's labels and named with the tab-title texts `pageTitle.<route>` (feature plan 0067) — names
 * like "Status" are ambiguous without their mode. A saved name that is not in the list (any more) is
 * ignored at start-up; here the selection shows "Wie bisher" with a hint, and the button is active so
 * the stale value can be removed.
 *
 * The second list (0069) appears for an area that takes targets. Its rows are loaded once per area
 * (start-page-targets.loader.ts). Only what cannot be derived is state; `gone` (the saved target no longer
 * exists) and `effective` (the target that a save would send) are derived on every render, so the order
 * in which lists arrive does not matter:
 * - HomeAction drops a deleted target and sets `startPageTargetGone`;
 * - a target deleted after the page was served is missing from the loaded list of the saved area.
 * While a list loads or failed, the chosen target is kept, so a save never removes it by accident.
 *
 * No leave warning: two fields only. Extends `AbstractO2dElement` (Light DOM, ADR-007); styles in
 * ralphy.css.
 */

import { AbstractO2dElement } from '@o2d/shared';
import { get } from 'lit-translate';
import { html } from 'lit';
import type { PropertyValues, TemplateResult } from 'lit';
import { customElement } from 'lit/decorators/custom-element.js';
import { state } from 'lit/decorators/state.js';
import { keyed } from 'lit/directives/keyed.js';
import { container } from 'tsyringe';
import { hasPrivilege } from '../helpers/privilege.helper';
import { CommandName } from '../commands/command-name.enum';
import { sanitizeTenant } from '../components/ha-app/tenant-context.helper';
import { START_PAGE_GROUPS, startPageAcceptsTarget, startPagePath } from '../components/ha-app/start-page.helper';
import { CommandBus } from '../services/command-bus.service';
import { GlobalNotificationService } from '../services/global-notification.service';
import { SaveTenantSettingsCommand } from '../commands/save-tenant-settings.command';
import { TenantSettingsContent } from '../commands/payloads/tenant-settings-content';
import { saveFailureKind } from '../helpers/save-failure-kind.helper';
import { loadStartPageTargets } from '../helpers/start-page-targets.loader';
import type { StartPageTargetRow } from '../helpers/start-page-targets.loader';
import { translateOr } from '../helpers/translate-or.helper';
import { UiHelper } from '../helpers/ui.helper';
import { NOTIFICATION_DURATION } from '../constants';

/** The targets of one area; an area that was never shown has no entry. */
type TargetList =
    | { readonly status: 'loading' }
    | { readonly status: 'loaded'; readonly rows: readonly StartPageTargetRow[] }
    | { readonly status: 'error' };

/** A saved setting as the selects compare it: the raw string, `''` when none is saved. */
function savedSetting(name: 'startPage' | 'startPageTarget'): string {
    const raw = window.__TENANT_SETTINGS__?.[name];
    return typeof raw === 'string' ? raw : '';
}

@customElement('ha-tenant-settings-view')
export class HaTenantSettingsView extends AbstractO2dElement {

    /** What is saved for the tenant (raw, possibly a name no longer in the list). */
    @state() private _saved = savedSetting('startPage');

    /** What the select shows: the saved name when it is selectable, else "Wie bisher". */
    @state() private _selected = startPagePath(this._saved) ? this._saved : '';

    /** The saved target, `''` when none (also when HomeAction dropped a deleted one). */
    @state() private _savedTarget = savedSetting('startPageTarget');

    /** The target the second list shows; may name a target that turns out to be gone. */
    @state() private _selectedTarget = this._selected === this._saved ? this._savedTarget : '';

    /** HomeAction found the saved target deleted. */
    @state() private _serverGone = window.__TENANT_SETTINGS__?.startPageTargetGone === true;

    @state() private _targets: Readonly<Partial<Record<string, TargetList>>> = {};

    @state() private _saving = false;

    protected override willUpdate(changed: PropertyValues): void {
        super.willUpdate(changed);
        // The first render shows the saved area, so its list is the first one loaded — which is what lets
        // `_gone()` notice a target deleted since the page was served.
        const area = this._selected;
        if (startPageAcceptsTarget(area) && !this._targets[area]) {
            void this._load(area);
        }
    }

    private async _load(area: string): Promise<void> {
        this._targets = { ...this._targets, [area]: { status: 'loading' } };
        try {
            const rows = [...await loadStartPageTargets(area)].sort((a, b) =>
                a.title.localeCompare(b.title, 'de') || a.uid.localeCompare(b.uid));
            this._targets = { ...this._targets, [area]: { status: 'loaded', rows } };
        } catch (error) {
            console.error(`Start page targets of ${area} could not be loaded`, error);
            this._targets = { ...this._targets, [area]: { status: 'error' } };
        }
    }

    /** The rows of an area once loaded, else `null`. */
    private _loadedRows(area: string): readonly StartPageTargetRow[] | null {
        const list = this._targets[area];
        return list?.status === 'loaded' ? list.rows : null;
    }

    /** The saved target no longer exists. */
    private _gone(): boolean {
        const rows = this._loadedRows(this._saved);
        return this._serverGone
            || (this._savedTarget !== '' && rows !== null && !rows.some(row => row.uid === this._savedTarget));
    }

    /** The target a save sends: `''` when the loaded list of the chosen area does not hold it. */
    private _effectiveTarget(): string {
        const rows = this._loadedRows(this._selected);
        return rows !== null && !rows.some(row => row.uid === this._selectedTarget) ? '' : this._selectedTarget;
    }

    private _canSave(): boolean {
        return !this._saving
            && (this._selected !== this._saved || this._effectiveTarget() !== this._savedTarget || this._gone());
    }

    private readonly _onChange = (event: Event): void => {
        this._selected = (event.target as HTMLSelectElement).value;
        this._selectedTarget = this._selected === this._saved ? this._savedTarget : '';
    };

    private readonly _onTargetChange = (event: Event): void => {
        this._selectedTarget = (event.target as HTMLSelectElement).value;
    };

    private readonly _onSave = async (): Promise<void> => {
        if (!this._canSave()) {
            return;
        }
        this._saving = true;
        const startPage = this._selected;
        const target = this._effectiveTarget();
        try {
            const tenant = sanitizeTenant(window.__APP_CONTEXT__);
            const command = new SaveTenantSettingsCommand(tenant ?? '', new TenantSettingsContent(startPage, target));

            // The CommandBus settles every failed send itself (ADR-016), as in the theme editor.
            const result = await container.resolve(CommandBus).dispatchCommandForResult(command);
            if (!result.success) {
                const forbidden = saveFailureKind(result.error) === 'forbidden';
                container.resolve(GlobalNotificationService).error(
                    get('tenantSettings.save.errorTitle'),
                    get(forbidden ? 'tenantSettings.save.forbiddenMessage' : 'tenantSettings.save.errorMessage'),
                );
                return;
            }

            const settings = { ...(window.__TENANT_SETTINGS__ ?? {}) };
            if (startPage === '') {
                delete settings.startPage;
            } else {
                settings.startPage = startPage;
            }
            if (target === '') {
                delete settings.startPageTarget;
            } else {
                settings.startPageTarget = target;
            }
            delete settings.startPageTargetGone;
            window.__TENANT_SETTINGS__ = settings;
            this._saved = startPage;
            this._savedTarget = target;
            this._selectedTarget = target;
            this._serverGone = false;

            UiHelper.notify({
                closable: true,
                level: 'info',
                title: get('tenantSettings.save.successTitle'),
                content: html`<p>${get('tenantSettings.save.successMessage')}</p>`,
                duration: NOTIFICATION_DURATION,
            });
        } finally {
            this._saving = false;
        }
    };

    protected render(): TemplateResult {
        // Defense in depth on top of the route guard (e.g. the tag instantiated directly).
        if (!hasPrivilege(CommandName.SAVE_TENANT_SETTINGS)) {
            return html``;
        }

        const unknownSaved = this._saved !== '' && !startPagePath(this._saved);
        const effective = this._effectiveTarget();
        const showGone = this._gone() && this._selected === this._saved && effective === '';

        return html`
            <div class="view__body tenant-settings">
                <h2 class="tenant-settings__title">${get('tenantSettings.title')}</h2>
                <p class="tenant-settings__hint">${get('tenantSettings.hint')}</p>
                <label class="tenant-settings__field">
                    <span class="tenant-settings__label">${get('tenantSettings.startPage.label')}</span>
                    <select class="tenant-settings__start-page" .value=${this._selected} @change=${this._onChange}>
                        <option value="" ?selected=${this._selected === ''}>${get('tenantSettings.startPage.lastUsed')}</option>
                        ${START_PAGE_GROUPS.map(group => html`
                            <optgroup label=${get(group.labelKey)}>
                                ${group.pages.map(page => html`
                                    <option value=${page.name} ?selected=${this._selected === page.name}>
                                        ${translateOr(`pageTitle.${page.name}`, page.name)}
                                    </option>
                                `)}
                            </optgroup>
                        `)}
                    </select>
                </label>
                ${unknownSaved
                    ? html`<p class="tenant-settings__unknown">${get('tenantSettings.startPage.unknown')}</p>`
                    : html``}
                ${startPageAcceptsTarget(this._selected) ? this._renderTargetField(effective) : html``}
                ${showGone
                    ? html`<p class="tenant-settings__unknown">${get('tenantSettings.startPageTarget.gone')}</p>`
                    : html``}
                <button class="tenant-settings__save" type="button"
                    ?disabled=${!this._canSave()}
                    @click=${this._onSave}>
                    ${get('tenantSettings.save.button')}
                </button>
            </div>
        `;
    }

    /** The second list: "Nur den Bereich öffnen", then the area's targets by title. */
    private _renderTargetField(effective: string): ReturnType<typeof keyed> {
        const area = this._selected;
        const list = this._targets[area];
        const rows = list?.status === 'loaded' ? list.rows : [];
        const labelKey = area === 'knowledge'
            ? 'tenantSettings.startPageTarget.knowledgeLabel'
            : 'tenantSettings.startPageTarget.linkviewLabel';
        // Until the rows are there the disabled list shows its only option; a save still sends `effective`.
        const shown = list?.status === 'loaded' ? effective : '';

        // Keyed by area: Linkview and Wissen would otherwise share one <select> whose <option> nodes Lit
        // reuses by position, and a reused node the user once picked keeps the browser's own selection —
        // back on the saved area the saved target showed as an empty choice (review 0069, run 03).
        return keyed(area, html`
            <label class="tenant-settings__field">
                <span class="tenant-settings__label">${get(labelKey)}</span>
                <select class="tenant-settings__start-page-target" .value=${shown}
                    ?disabled=${list?.status !== 'loaded'}
                    @change=${this._onTargetChange}>
                    <option value="" ?selected=${shown === ''}>${get('tenantSettings.startPageTarget.areaOnly')}</option>
                    ${rows.map(row => html`
                        <option value=${row.uid} ?selected=${shown === row.uid}>${this._targetLabel(row)}</option>
                    `)}
                </select>
            </label>
            ${list?.status === 'error'
                ? html`<p class="tenant-settings__unknown">${get('tenantSettings.startPageTarget.loadError')}</p>`
                : html``}
        `);
    }

    /** The title, for knowledge objects with their kind ("Whiteboard", "Textdokument", "Bild"). */
    private _targetLabel(row: StartPageTargetRow): string {
        if (!row.type) {
            return row.title;
        }
        return `${row.title} (${translateOr(`wissen.dialog.knowledge-object-edit.type-${row.type}`, row.type)})`;
    }
}
